import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteAccount } from "src/db/serviceAccount";
import type { MarketplaceAccount } from "../../../types/marketplaceAccount";

type Props = { account: MarketplaceAccount; onChanged: () => void };

type ActionButtonProps = {
  label: string;
  icon: string;
  disabled?: boolean;
  onClick: () => void;
  tone?: "default" | "danger";
};

function ActionButton({ label, icon, disabled, onClick, tone = "default" }: ActionButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-border tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${tone === "danger" ? "tw-border-red-200 tw-text-red-600 hover:tw-bg-red-50 dark:tw-border-red-900 dark:hover:tw-bg-red-950/30" : "tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-100 dark:tw-border-slate-700 dark:tw-text-slate-300 dark:hover:tw-bg-slate-800"}`}
    >
      <span className="material-symbols-outlined tw-text-lg">{icon}</span>
    </button>
  );
}

export function MarketplaceAccountActions({ account, onChanged }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const execute = async (operation: () => Promise<void>) => {
    setBusy(true);
    try {
      await operation();
      onChanged();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Marketplace account action failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    if (window.confirm(`Delete ${account.name || account.type}? This cannot be undone.`)) {
      void execute(() => deleteAccount(account.id));
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
      <ActionButton label="Edit marketplace" icon="edit" disabled={busy} onClick={() => navigate(`/setting/marketplace/edit/${account.id}`)} />
      <ActionButton label="Delete marketplace" icon="delete" disabled={busy} onClick={remove} tone="danger" />
    </div>
  );
}
